import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardHeader from "../../components/app_components/DashboardHeader";
import DashboardSidebar from "../../components/app_components/DashboardSidebar";
import { Home, Bell, BellDot, ShoppingBag, Settings, Users, ShoppingCart, Zap, Calendar, RefreshCw, XCircle, Share2 } from 'lucide-react';
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from '../../App'; // Import the auth hook

const Partners = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, httpClient, logout } = useAuth(); // Get auth context
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  // Add premium status state
  const [isPremium, setIsPremium] = useState(false);

  const fetchUnreadNotifications = async () => {
    try {
      if (!httpClient || !user) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Fetch premium status function
  const fetchPremiumStatus = async () => {
    try {
      if (!user) {
        setIsPremium(false);
        return;
      }

      // Try dedicated premium status endpoint
      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      // Fallback to user account endpoint
      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        setIsPremium(fallbackData.userPlan === 'premium');
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/demo");
      return;
    }
    
    fetchUnreadNotifications();
    fetchPremiumStatus(); // Fetch premium status
    const interval = setInterval(() => {
      fetchUnreadNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, httpClient]);

  const navItems = [
    { icon: Home, label: t('partners.nav.home'), path: "/dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('partners.nav.notifications'),
      path: "/notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('partners.nav.marketplace'), path: "/marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs'), path: "/share-subs" },
    { icon: ShoppingBag, label: t('partners.nav.connectBank'), path: "/connectBank" },
    { icon: Users, label: t('partners.nav.partners'), path: "/partners" },
    { icon: Settings, label: t('partners.nav.settings'), path: "/account-settings" },
  ];

  const partnerFeatures = [
    {
      icon: Zap,
      title: t('partners.features.activateServices.title'),
      description: t('partners.features.activateServices.description')
    },
    {
      icon: RefreshCw,
      title: t('partners.features.changePlans.title'),
      description: t('partners.features.changePlans.description')
    },
    {
      icon: XCircle,
      title: t('partners.features.cancelAnytime.title'),
      description: t('partners.features.cancelAnytime.description')
    },
    {
      icon: Calendar,
      title: t('partners.features.billingManagement.title'),
      description: t('partners.features.billingManagement.description')
    }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <DashboardSidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        navItems={navItems}
        currentPath={location.pathname}
        navigate={navigate}
        user={user}
        onLogout={logout}
      />

      <div className="flex-1 overflow-auto">
        <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />
        
        <div className="p-6 space-y-6">
          {/* Updated with conditional premium gradient */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-6 rounded-lg transition-all duration-500`}>
            <h1 className="text-2xl font-bold mb-2">{t('partners.title')}</h1>
            <p className="text-white/90">{t('partners.subtitle')}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="text-center py-8">
              <Users className="h-16 w-16 mx-auto text-[#253985] mb-4" />
              <h2 className="text-3xl font-bold text-gray-800 mb-3">{t('partners.comingSoon.title')}</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('partners.comingSoon.description')}
              </p>
              
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {partnerFeatures.map((feature, index) => (
                  <div key={index} className="p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <feature.icon className="h-8 w-8 text-[#253985] mb-3" />
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Partners;